// MessageComponents.js
import { motion } from 'framer-motion';
import AudioMessagesPlayer from './AudioMessagesPlayer';
import ImageGallery from './ImageGallery';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from "rehype-raw";
import './MessageComponents.css'


export const MediaMessageUserSending = () => {
  return (
    <div className="sending-dots" aria-label="Sending…">
      <span></span><span></span><span></span>
    </div>
  );
};

// 1. Assistant header message (intro message)
// (This one is left intact because its wrapper is separate.)
export const AssistantHeaderMessage = ({ message }) => {
  return (
    <motion.div
      className="assistant-header"
      exit={{ display: "none" }}
    >
      {message.type === 'bot' ? (
        <>
          {message.replacebotlogo === 'yes' ? (
            <img className="avatar" src={message.logo} alt={message.newassistant} />
          ) : (
            <motion.div
              className="avatar bot"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.1 }}
            />
          )}
          <div className="assistant-info">
            <div className={message.showbotbadge === 'yes' ? 'bot-info' : 'name'}>
              {message.newassistant}
            </div>
            <div className="assistant-subtitle">{message.subscription}</div>
          </div>
        </>
      ) : (
        <>
          <img className="avatar" src={message.logo} alt={message.newassistant} />
          <div className="assistant-info">
            <div className="name">{message.newassistant}</div>
            <div className="assistant-subtitle">{message.subscription}</div>
          </div>
        </>
      )}
    </motion.div>
  );
};

/**
 * For all media and HTML components, we now return only the internal markup
 * that goes inside the message-bubble div.
 */

// 2. Media message from bot – URL
export const MediaMessageBotURL = ({ message }) => {
  return (
    <>
      <p dangerouslySetInnerHTML={{ __html: message.text }}></p>
      <a className='link-wrapper' href={message.url} target="_blank" rel="noopener noreferrer">
        <div className='link-container'>
          <img className='link-image' src={message.image} alt={message.url} />
          <div className='link-description'>
            <span className='description-title'>{message.title}</span>
            <div className='description-text'>{message.description}</div>
          </div>
        </div>
      </a>
    </>
  );
};

// 3. Media message from bot – Image (single or gallery)
export const MediaMessageBotImage = ({ message }) => {
  return (
    <>
      <ImageGallery message={message} />
      {message.text && (
        <div className='gallery-message'>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              )
            }}
          >
            {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
};

// 4. Media message from bot – Audio
// export const MediaMessageBotAudio = ({ message }) => {
//     const dataUrlSrc = `data:audio/mp4;base64,${message.audioBinary}`;
//   return (
//     <AudioMessagesPlayer
//       audioSrc={dataUrlSrc}
//       histogramData={message.histogram || []}
//       playbackTime={message.playbackTime}
//     />
//   );
// };

// test for mobiles
export const MediaMessageBotAudio = ({ message }) => {
  const audioSrc =
    message.audio.base64 ||
    message.audioBlobUrl ||
    message.file ||
    (message.audioBinary ? `data:audio/mp4;base64,${message.audioBinary}` : "");
  return (
    <AudioMessagesPlayer
      audioSrc={audioSrc}
      histogramData={message.histogram || message.audio.histogram || []}
      playbackTime={message.playbackTime || message.audio.playback_time}
    />
  );
};

// 5. Media message from bot – File
export const MediaMessageBotFile = ({ message }) => {
    const file = message.files[0] || message;
    const ext = file.extension || file.fileName.split('.').pop();
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <a
        className="file-download-link"
        href={file.base64 || file.url}
        target="_blank"
        rel="noopener noreferrer"
        download={file.fileName}
      ></a>
        <div className={`file-wrapper file-${ext}`}>
          <a
            className='inmessage-download-link'
            href={file.base64 || file.url}
            target="_blank"
            rel="noopener noreferrer"
            download={file.fileName}
          >
            <div className='file-info'>
              <div className="file-name">{file.fileName}</div>
              <div className="file-creds">
                <span className='file-ext'>{ext}</span><span>Document</span>
                <span className='file-separator'></span>
                <span className='file-size'>{file.size}</span><span>MB</span>
              </div>
            </div>
            <div className="file-icon"></div>
          </a>
        </div>

    </>
  );
};

// 6. Media message from bot – Video
export const MediaMessageBotVideo = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <video controls src={message.url} />
      <div className="playback-time">Duration: {message.playbackTime}s</div>
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};

// 7. Media message from bot – HTML
export const MediaMessageBotHTML = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <div className="html-content" dangerouslySetInnerHTML={{ __html: message.code }} />
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};

/**
 * ----------------------------
 * Outbound Messages (User)
 * -----------------x-----------
 */

// 8. Media message from user – URL
export const MediaMessageUserURL = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <a className='link-wrapper' href={message.url} target="_blank" rel="noopener noreferrer">
        <div className='link-container'>
          <img className='link-image' src={message.image} alt={message.url} />
          <div className='link-description'>
            <span className='description-title'>{message.title}</span>
            <div className='description-text'>
              <div className='text-container'>{message.description}</div>
            </div>
          </div>
        </div>
      </a>
    </>
  );
};

// 9. Media message from user – Image (single or gallery)
export const MediaMessageUserImage = ({ message }) => {
  return (
    <>
      <ImageGallery message={message} />
      {message.text && (
        <div className='gallery-message'>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              )
            }}
          >
            {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
          </ReactMarkdown>
        </div>
      )}
    </>
  );
};

// test for mobiles
export const MediaMessageUserAudio = ({ message }) => {
  const audioSrc =
    message.audioBlobUrl ||
    message.file ||
    (message.audioBinary ? `data:audio/mp4;base64,${message.audioBinary}` : "");

  return (
    <AudioMessagesPlayer
      audioSrc={audioSrc}
      histogramData={message.histogram || []}
      playbackTime={message.playbackTime}
    />
  );
};

// 11. Media message from user – File
export const MediaMessageUserFile = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <a
        className="file-download-link"
        href={message.url}
        target="_blank"
        rel="noopener noreferrer"
        download={message.fileName}
      ></a>
        <div className={`file-wrapper file-${message.fileName.split('.').pop()}`}>
          <a
            className="inmessage-download-link"
            href={message.url}
            target="_blank"
            rel="noopener noreferrer"
            download={message.fileName}
          >
            <div className='file-info'>
              <div className="file-name">{message.fileName}</div>
              <div className="file-creds">
                <span className='file-ext'>{message.fileName.split('.').pop()}</span><span>Document</span>
                <span className='file-separator'></span>
                <span className='file-size'>{message.size}</span><span>MB</span>
              </div>
            </div>
            <div className="file-icon"></div>
          </a>
        </div>
      
    </>
  );
};

// 12. Media message from user – Video
export const MediaMessageUserVideo = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <video controls src={message.url} />
      <div className="playback-time">Duration: {message.playbackTime}s</div>
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};

// 13. Media message from user – HTML
export const MediaMessageUserHTML = ({ message }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          )
        }}
      >
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>
      <div className="html-content" dangerouslySetInnerHTML={{ __html: message.code }} />
      <span className="timestamp">{message.timestamp}</span>
    </>
  );
};
